import {Observable} from 'rxjs';
import {shareReplay} from 'rxjs/operators';
import {CacheService} from '../services/store/cache.service';

export interface ICacheService {
  setObservableItem<T>(key: string, item: Observable<T>, timeExpired?: number): void;

  getObservableItem<T>(key: string): Observable<T> | undefined;

  getObservableCacheNames(): string[];

  hasObservableItem(key: string): boolean;

  deleteObservableItem(key: string): void;

  clearObservableItems(): void;

  addAndCheckObservableLimit(options: ICacheOptions, key: string): void;

  setItem<T>(key: string, item: T): void;

  getItem<T>(key: string): T | undefined;

  getCacheNames(): string[];

  hasItem(key: string): boolean;

  deleteItem(key: string): void;

  clearItems(): void;

  addAndCheckLimit(options: ICacheOptions, key: string): void;
}

export interface ICacheOptions {
  name?: string;
  limit?: number;
  time?: { hours?: number; minutes?: number; secondes?: number };
}

interface ICacheEvictOptions {
  name: string;
  match: 'full' | 'withoutParams';
}

function syncCacheable<T>(
  options: ICacheOptions,
  originalMethod: (this: unknown, arg: unknown) => T,
): (this: unknown, ...args: unknown[]) => T {
  return function (this: unknown, ...args: unknown[]): T {
    const service = CacheService.INSTANCE;
    const cacheName = `${options.name}${args.length > 0 ? '#' + JSON.stringify(args) : ''}`;
    if (!service.hasItem(cacheName)) {
      const result = originalMethod.apply(this, args as [unknown]) ?? null; // Utilisation du nullish coalescing operator
      service.setItem(cacheName, result);
      service.addAndCheckLimit(options, cacheName);
    }    // Utilisation du non-null assertion operator (!) pour indiquer que getItem ne renverra pas undefined
    return service.getItem<T>(cacheName);
  };
}

function observableCacheable<T>(
  options: ICacheOptions,
  originalMethod: (this: unknown, ...args: unknown[]) => Observable<T>,
): (this: unknown, ...args: unknown[]) => Observable<T> {
  return function (this: unknown, ...args: unknown[]): Observable<T> {
    const service = CacheService.INSTANCE;
    const cacheName = `${options.name}${args?.length > 0 ? '#' + JSON.stringify(args) : ''}`;
    if (!service.hasObservableItem(cacheName)) {
      const result = originalMethod.apply(this, args) ?? new Observable<T>();
      if (options.time) {
        const time = (options.time.hours ? options.time.hours : 0) * 3600000 +
          (options.time.minutes ? options.time.minutes : 0) * 60000 + (options.time.secondes ? options.time.secondes : 0) * 1000;
        service.setObservableItem(cacheName, result.pipe(shareReplay(1)), time);
      } else {
        service.setObservableItem(cacheName, result.pipe(shareReplay(1)));
      }
      service.addAndCheckObservableLimit(options, cacheName);
    }
    return service.getObservableItem(cacheName);
  };
}

function cacheableEvict<T>(
  options: ICacheEvictOptions,
  originalMethod: (this: unknown, ...args: unknown[]) => Observable<T>,
): (this: unknown, ...args: unknown[]) => Observable<T> {
  return function (this: unknown, ...args: unknown[]): Observable<T> {
    const service = CacheService.INSTANCE;
    service.getCacheNames().filter(value => {
      cacheNameEquals(value, options, args);
    }).forEach(value => {
      service.deleteItem(value);
    });
    return originalMethod.apply(this, args);
  };
}

function observableCacheableEvict<T>(
  options: ICacheEvictOptions,
  originalMethod: (this: unknown, ...args: unknown[]) => Observable<T>,
): (this: unknown, ...args: unknown[]) => Observable<T> {
  return function (this: unknown, ...args: unknown[]): Observable<T> {
    const service = CacheService.INSTANCE;
    service.getObservableCacheNames().filter(value => cacheNameEquals(value, options, args)).forEach(value => {
      service.deleteObservableItem(value);
    });
    return originalMethod.apply(this, args);
  };
}

function cacheNameEquals(cacheName: string, options: ICacheEvictOptions, args: unknown[]): boolean {
  if (options.match === 'full') {
    const [name, params] = cacheName?.split('#') ?? [];
    return name === options.name && params === JSON.stringify(args);
  }
  if (options.match === 'withoutParams') {
    return cacheName?.split('#')?.[0] === options.name;
  }
  return false;
}

// Decorator
export function Cacheable(options: ICacheOptions) {
  return (target: { constructor: { name: string } }, methodName: string, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;
    if (!options.name) {
      options.name = `${target.constructor.name}_${methodName}`;
    }
    descriptor.value = syncCacheable(options, originalMethod);
    return descriptor;
  };
}

export function CacheableObservable(options: ICacheOptions) {
  return (target: { constructor: { name: string } }, methodName: string, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;
    if (!options.name) {
      options.name = `${target.constructor.name}_${methodName}`;
    }
    descriptor.value = observableCacheable(options, originalMethod);
    return descriptor;
  };
}

export function CacheableEvict(options: ICacheEvictOptions) {
  return (target: unknown, methodName: string, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;
    descriptor.value = cacheableEvict(options, originalMethod);
    return descriptor;
  };
}

export function CacheableObservableEvict(options: ICacheEvictOptions) {
  return (target: unknown, methodName: string, descriptor: PropertyDescriptor) => {
    const originalMethod = descriptor.value;
    descriptor.value = observableCacheableEvict(options, originalMethod);
    return descriptor;
  };
}
